import * as React from 'react';
import styled, { css } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { Icon } from '../../primitives/Icon';
import { rem } from '../../tokens/spacing';

export type BannerVariant = 'info' | 'success' | 'warning' | 'error';
export type BannerPosition = 'inline' | 'fixed';

export interface BannerProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: BannerVariant;
  /** @default 'inline' — rounded card. 'fixed' renders a full-width strip pinned below the navbar. */
  position?: BannerPosition;
  /** CSS top offset when position='fixed'. Defaults to '67px' (standard navbar height). */
  fixedTop?: string;
  dismissible?: boolean;
  onDismiss?: () => void;
  icon?: string;
  semanticId?: string;
}

const variantStyles = (variant: BannerVariant) => {
  switch (variant) {
    case 'success':
      return css`
        background: var(--ui-status-success-muted);
        color: var(--ui-text);
        border-color: var(--ui-status-success);
      `;
    case 'warning':
      return css`
        background: var(--ui-banner-warning-bg, #fef3cd);
        color: var(--ui-banner-warning-fg, #664d03);
        border-color: var(--ui-banner-warning-border, #ffc107);

        .ui-banner__icon {
          color: var(--ui-banner-warning-icon, #997404);
        }
      `;
    case 'error':
      return css`
        background: var(--ui-status-danger-muted);
        color: var(--ui-text);
        border-color: var(--ui-status-danger);
      `;
    case 'info':
    default:
      return css`
        background: var(--ui-status-info-muted);
        color: var(--ui-text);
        border-color: var(--ui-status-info);
      `;
  }
};

const fixedStyles = css<{ $fixedTop: string }>`
  position: fixed;
  top: ${({ $fixedTop }) => $fixedTop};
  left: 0;
  z-index: 997;
  width: 100%;
  border-radius: 0;
  border-top: none;
  border-left: none;
  border-right: none;
  border-bottom-width: 1px;
  border-bottom-style: solid;
  justify-content: center;
  padding: ${rem(8)} ${rem(20)};
  font-size: ${rem(13)};
  line-height: 1.4;

  & > .ui-banner__content {
    flex: 0 1 auto;
  }

  @media (max-width: 512px) {
    font-size: ${rem(11)};
    padding: ${rem(6)} ${rem(12)};
  }
`;

const shouldForward = (prop: string | number): boolean =>
  !['$variant', '$position', '$fixedTop'].includes(String(prop));

const Root = styled.div.withConfig({ shouldForwardProp: shouldForward })<{
  $variant: BannerVariant;
  $position: BannerPosition;
  $fixedTop: string;
}>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: var(--ui-space-sm, ${rem(8)});
  width: 100%;
  padding: ${rem(12)} ${rem(16)};
  border-radius: var(--ui-radius-md, ${rem(6)});
  border: 1px solid transparent;
  font-family: var(--ui-font-sans);
  font-size: ${rem(14)};
  line-height: 1.5;

  ${({ $variant }) => variantStyles($variant)}
  ${({ $position, $fixedTop }) => $position === 'fixed' && fixedStyles}
`;

const Content = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const DismissButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${rem(4)};
  margin: ${rem(-4)} ${rem(-4)} ${rem(-4)} 0;
  border: none;
  border-radius: var(--ui-radius-sm);
  background: transparent;
  color: inherit;
  cursor: pointer;
  opacity: 0.85;

  &:hover {
    opacity: 1;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px var(--ui-focus-ring);
  }
`;

export const Banner = React.forwardRef<HTMLDivElement, BannerProps>(function Banner(
  {
    variant = 'info',
    position = 'inline',
    fixedTop = '67px',
    dismissible = false,
    onDismiss,
    icon,
    semanticId,
    children,
    ...rest
  },
  ref,
) {
  const semanticProps = getSemanticAttributes(semanticId);
  const defaultIcon =
    variant === 'success'
      ? 'check_circle'
      : variant === 'warning'
        ? 'warning'
        : variant === 'error'
          ? 'error'
          : 'info';

  return (
    <Root
      ref={ref}
      $variant={variant}
      $position={position}
      $fixedTop={fixedTop}
      role="alert"
      data-variant={variant}
      data-position={position}
      {...semanticProps}
      {...rest}
    >
      <Icon name={icon != null && icon !== '' ? icon : defaultIcon} size="sm" aria-hidden className="ui-banner__icon" />
      <Content className="ui-banner__content">{children}</Content>
      {dismissible ? (
        <DismissButton type="button" aria-label="Dismiss" onClick={onDismiss}>
          <Icon name="close" size="sm" aria-hidden />
        </DismissButton>
      ) : null}
    </Root>
  );
});

Banner.displayName = 'Banner';
